/**
 * Desenho do mapa.
 *
 * As quatro camadas de tiles são cozidas numa textura só, uma vez, ao carregar
 * o mapa. Um mapa de 60×60 tem 3600 casas e cada autotile vira quatro
 * quadrantes: manter 57 mil sprites vivos custaria caro a cada quadro para
 * desenhar algo que não muda. O que muda — a superfície da água — fica num
 * container à parte, redesenhado só quando o quadro da animação vira.
 */

import {
  Assets,
  Container,
  RenderTexture,
  Sprite,
  Texture,
  TilingSprite,
  type Renderer,
} from 'pixi.js';
import { ASSETS_BASE, frameRect, type ImageEntry, type Manifest } from '../assets/manifest.js';
import { frameTexture } from './frames.js';
import { TILE_SIZE, isAnimated, tilePieces, type SheetNumber, type TilePiece } from './tiles.js';

export interface TilesetDef {
  readonly id: number;
  readonly name: string;
  readonly sheets: readonly string[];
  readonly flags: readonly number[];
}

/**
 * Uma unidade que o mapa já traz posicionada — o editor da Etapa 7 as grava
 * junto com o terreno, e a escaramuça as usa no lugar do esquadrão padrão.
 */
export interface MapPlacementData {
  readonly name?: string | undefined;
  readonly team?: string | undefined;
  readonly className?: string | undefined;
  readonly weaponName?: string | undefined;
  readonly level?: number | undefined;
  readonly at?: { readonly x: number; readonly y: number } | undefined;
  readonly isCommander?: boolean | undefined;
  readonly aiProfile?: string | undefined;
  readonly portrait?: { readonly sheet: string; readonly index: number } | undefined;
  readonly sprite?: { readonly sheet: string; readonly index: number } | undefined;
  readonly battler?: string | undefined;
}

/**
 * Um elemento desenhado do mapa: porta, baú, tocha, estátua, aldeão, monstro.
 *
 * No RPG Maker eles são "eventos", e a conversão trazia só as camadas de tiles
 * — o que deixava de fora quase mil desenhos espalhados pelos 56 mapas. São
 * cenário construído à mão; sem eles o mapa chega ao jogo pela metade.
 *
 * Só a aparência atravessa. O comportamento de um evento é um script de RPG
 * (abrir loja, iniciar diálogo, transferir de mapa) e não teria onde encaixar
 * num tático por turnos.
 */
export interface MapEventData {
  readonly x: number;
  readonly y: number;
  /** 0 desenha abaixo das unidades, 1 junto, 2 por cima. */
  readonly priority: number;
  /** Era sólido no jogo de origem. A anotação tática continua sendo do editor. */
  readonly blocks?: boolean | undefined;
  readonly name?: string | undefined;
  /** Folha de `characters`, quando o elemento é um sprite. */
  readonly sheet?: string | undefined;
  readonly index?: number | undefined;
  /** Direção do acervo: 2 baixo, 4 esquerda, 6 direita, 8 cima. */
  readonly direction?: number | undefined;
  readonly pattern?: number | undefined;
  /** Quando o elemento é um tile do próprio tileset, e não uma folha. */
  readonly tileId?: number | undefined;
  /** Anima parado — a tocha que pisca, a bandeira que balança. */
  readonly animated?: boolean | undefined;
}

export interface MapObjectiveData {
  readonly kind: string;
  readonly turns?: number | undefined;
  readonly at?: { readonly x: number; readonly y: number } | undefined;
}

export interface MapData {
  readonly id: number;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly tilesetId: number;
  readonly layers: readonly (readonly number[])[];
  readonly shadows: readonly number[];
  readonly terrain: readonly number[];
  readonly terrainTypes: readonly string[];
  /* --- ambiente, vindo do acervo ou completado pelo pipeline --- */
  /** Faixa da trilha do mapa, no nome do acervo. */
  readonly bgm?: string | undefined;
  /** Cenário do combate lateral: chão e horizonte. */
  readonly battleback?: string | undefined;
  readonly battleback2?: string | undefined;
  /**
   * Pares de casas que se ligam sem serem vizinhas.
   *
   * Muitos mapas do acervo são cômodos que só se alcançavam por evento de
   * porta; sem estes elos, metade deles é um punhado de ilhas onde os dois
   * esquadrões nunca se encontram.
   */
  readonly teleports?: readonly TeleportData[] | undefined;
  /** Paisagem por trás do mapa — o céu do Templo Flutuante, por exemplo. */
  readonly parallax?: string | undefined;
  /** Os elementos desenhados: portas, baús, tochas, estátuas, gente. */
  readonly events?: readonly MapEventData[] | undefined;
  /* --- anotação tática, opcional: mapas do acervo ainda não a têm --- */
  readonly placements?: readonly MapPlacementData[] | undefined;
  readonly objective?: MapObjectiveData | undefined;
  readonly chests?: readonly MapChestData[] | undefined;
}

/**
 * Um baú anotado no mapa.
 *
 * O que o editor grava; o que a partida faz com ele — quem pode abrir, o que
 * sai de dentro — é regra do motor, em `@oz/rules`.
 */
export interface MapChestData {
  readonly at: { readonly x: number; readonly y: number };
  /**
   * O que está dentro, nas duas formas da bolsa: uma ARMA (`weaponId`) ou um
   * ITEM do RPG (`itemId`) — a poção, o antídoto, a chave. Ver `Chest` no
   * motor: o id 7 é a Poção entre os itens e o Cajado de Madeira entre as
   * armas, e por isso são dois campos e não um id solto.
   */
  readonly weaponId?: number | undefined;
  readonly itemId?: number | undefined;
  /** Trancado exige chave ou ladrão. Ausente é trancado. */
  readonly locked?: boolean | undefined;
}

export interface TeleportData {
  readonly a: { readonly x: number; readonly y: number };
  readonly b: { readonly x: number; readonly y: number };
}

const WATER_FRAMES = 4;
const WATER_FRAME_MS = 400;

/** Carrega as folhas de um tileset, na numeração que o desenho espera. */
async function loadSheets(
  manifest: Manifest,
  tileset: TilesetDef,
): Promise<Map<SheetNumber, Texture>> {
  const sheets = new Map<SheetNumber, Texture>();

  await Promise.all(
    tileset.sheets.map(async (name, index) => {
      if (!name) return;
      const entry = manifest.images['tilesets']?.[name];
      if (!entry) return;
      const texture = await Assets.load<Texture>(`${ASSETS_BASE}/img/tilesets/${entry.file}`);
      sheets.set(index as SheetNumber, texture);
    }),
  );

  return sheets;
}

/** O recorte de um pedaço de tile, ou nulo quando não há o que desenhar. */
function pieceTexture(sheets: Map<SheetNumber, Texture>, piece: TilePiece): Texture | null {
  const sheet = sheets.get(piece.sheet);
  if (!sheet) return null;

  // Um retângulo que estoure a folha vira textura vazia e some sem aviso — o
  // recorte é conferido antes de virar sprite.
  const width = sheet.source.width;
  const height = sheet.source.height;
  if (piece.sx < 0 || piece.sy < 0 || piece.sx + piece.sw > width || piece.sy + piece.sh > height) {
    return null;
  }

  return frameTexture(sheet.source, piece.sx, piece.sy, piece.sw, piece.sh);
}

function pieceSprite(sheets: Map<SheetNumber, Texture>, piece: TilePiece): Sprite | null {
  const texture = pieceTexture(sheets, piece);
  if (!texture) return null;

  const sprite = new Sprite(texture);
  sprite.position.set(piece.dx, piece.dy);
  return sprite;
}

/**
 * Um tile de água já montado.
 *
 * A posição de cada quadrante não muda com a maré, só o recorte — e os
 * recortes dos quatro quadros já saem calculados, para a virada ser só uma
 * troca de referência.
 */
interface WaterTile {
  readonly sprites: readonly Sprite[];
  /** `frames[quadro][quadrante]`; nulo quando o quadro cai fora da folha. */
  readonly frames: readonly (readonly (Texture | null)[])[];
}

/** O desenho do elemento muda com o quadro da animação? */
function animates(event: MapEventData): boolean {
  // Gráfico de tile anima quando é água; o de folha, quando o acervo manda.
  return event.tileId ? isAnimated(event.tileId) : Boolean(event.animated);
}

/**
 * Onde fica, na folha, o quadro de um elemento do mapa.
 *
 * Uma folha de `characters` guarda oito personagens de doze quadros: três poses
 * de caminhada em quatro direções. O acervo numera a direção 2/4/6/8 (baixo,
 * esquerda, direita, cima), e é dela que sai a linha.
 */
function eventFrame(entry: ImageEntry, index: number, direction: number, pattern: number): number {
  const grid = entry.grid;
  if (!grid) return 0;

  const perSheet = grid.charactersPerSheet ?? 1;
  const charColumn = perSheet === 1 ? 0 : index % 4;
  const charRow = perSheet === 1 ? 0 : Math.floor(index / 4);

  // 2 → 0, 4 → 1, 6 → 2, 8 → 3. Uma direção fora da tabela vira "baixo".
  const directionRow = Math.min(3, Math.max(0, Math.floor(direction / 2) - 1));

  const column = charColumn * 3 + Math.min(2, Math.max(0, pattern));
  const row = charRow * 4 + directionRow;
  return row * grid.columns + column;
}

/**
 * O sprite de um elemento, já posicionado na casa.
 *
 * O acervo ancora o desenho pelo pé, no centro da casa, e sobe seis pixels
 * quem não é objeto — é essa folga que faz o aldeão encostar no tile de cima em
 * vez de flutuar dentro do próprio quadrado. Folhas com `!` no nome são objetos
 * (porta, baú, cristal) e ficam rentes à casa, sem a folga.
 */
function eventSprite(
  manifest: Manifest,
  event: MapEventData,
  frameOffset: number,
): Sprite | null {
  const sheet = event.sheet;
  if (!sheet) return null;

  const entry = manifest.images['characters']?.[sheet];
  if (!entry?.grid) return null;

  const pattern = event.animated
    ? [0, 1, 2, 1][frameOffset % 4] ?? 1
    : (event.pattern ?? 1);

  const index = eventFrame(entry, event.index ?? 0, event.direction ?? 2, pattern);
  const rect = frameRect(entry.grid, index);
  if (rect.x + rect.width > entry.width || rect.y + rect.height > entry.height) return null;

  /*
   * A folha precisa já estar carregada.
   *
   * `Assets.get` devolve `undefined` para o que ainda não foi pedido, e ler
   * `.source` disso estoura — uma exceção dentro do desenho derruba a árvore
   * React inteira, deixando a página em branco. Os elementos do mapa passam por
   * `#loadEventSheets` antes; quem desenha fora desse caminho (o baú, que não é
   * evento do mapa) precisa carregar a sua.
   */
  const texture = Assets.get<Texture>(`${ASSETS_BASE}/img/characters/${entry.file}`);
  if (!texture?.source) return null;

  const sprite = new Sprite(frameTexture(texture.source, rect.x, rect.y, rect.width, rect.height));

  const shiftY = sheet.startsWith('!') ? 0 : 6;
  sprite.position.set(
    event.x * TILE_SIZE + TILE_SIZE / 2 - rect.width / 2,
    event.y * TILE_SIZE + TILE_SIZE - shiftY - rect.height,
  );
  return sprite;
}

/**
 * O desenho de um baú, fechado ou aberto.
 *
 * Sai da folha `!Chest` do acervo, que é a mesma que o RPG de origem usa: a
 * primeira linha é o baú fechado e a última é o aberto. A marca `!` diz "colado
 * à casa, sem sombra", e é por isso que ele não recebe o deslocamento vertical
 * que as folhas de personagem recebem.
 *
 * Sem a folha no acervo — um jogo importado pode não trazê-la —, devolve nulo,
 * e o baú continua existindo como REGRA: quem passar por cima ainda abre. Um
 * desenho que falta não pode custar o item ao jogador.
 */
export function chestSheetName(manifest: Manifest): string | null {
  return (
    ['!Chest', 'Chest', '!SF_Chest'].find((name) => manifest.images['characters']?.[name]) ?? null
  );
}

/** Carrega a folha do baú. O desenho só acontece depois que ela chega. */
export async function loadChestSheet(manifest: Manifest): Promise<void> {
  const sheet = chestSheetName(manifest);
  const entry = sheet ? manifest.images['characters']?.[sheet] : null;
  if (!entry) return;
  try {
    await Assets.load<Texture>(`${ASSETS_BASE}/img/characters/${entry.file}`);
  } catch {
    // Uma folha que falta não pode custar o baú ao jogador: ele continua
    // existindo como regra, só não aparece.
  }
}

export function chestSprite(
  manifest: Manifest,
  at: { readonly x: number; readonly y: number },
  opened: boolean,
): Sprite | null {
  const sheet = chestSheetName(manifest);
  if (!sheet) return null;

  return eventSprite(
    manifest,
    {
      x: at.x,
      y: at.y,
      priority: 1,
      sheet,
      index: 0,
      // A folha do RPG Maker guarda as poses em direções: baixo é o baú
      // fechado, cima é o aberto.
      direction: opened ? 8 : 2,
      pattern: 1,
    },
    0,
  );
}

export class TilemapView {
  readonly container = new Container();

  /**
   * Os elementos que o acervo manda desenhar POR CIMA de quem passa.
   *
   * Fica de fora do `container` de propósito: quem monta a cena precisa
   * pendurá-lo acima da camada de unidades, senão a copa da árvore e o alto do
   * portal ficariam atrás do soldado que deveria estar passando por baixo.
   */
  readonly overlay = new Container();

  #animatedLayer = new Container();
  #eventLayer = new Container();
  #water: WaterTile[] = [];
  /** Vagas dos elementos que animam: refeitas a cada quadro, no mesmo lugar da pilha. */
  #animatedEvents: { readonly event: MapEventData; readonly slot: Container }[] = [];
  #parallax: TilingSprite | null = null;
  #baked: Sprite | null = null;
  #sheets = new Map<SheetNumber, Texture>();
  #map: MapData | null = null;
  #manifest: Manifest | null = null;
  #frame = 0;
  #elapsed = 0;

  async load(
    renderer: Renderer,
    manifest: Manifest,
    map: MapData,
    tileset: TilesetDef,
  ): Promise<void> {
    this.#map = map;
    this.#manifest = manifest;
    this.#sheets = await loadSheets(manifest, tileset);
    await Promise.all([
      this.#loadEventSheets(manifest, map),
      this.#loadParallax(manifest, map),
    ]);

    const scratch = new Container();
    for (let layer = 0; layer < map.layers.length; layer++) {
      for (let index = 0; index < map.width * map.height; index++) {
        const tileId = map.layers[layer]?.[index] ?? 0;
        if (tileId === 0 || isAnimated(tileId)) continue;

        const dx = (index % map.width) * TILE_SIZE;
        const dy = Math.floor(index / map.width) * TILE_SIZE;
        for (const piece of tilePieces(tileId, dx, dy)) {
          const sprite = pieceSprite(this.#sheets, piece);
          if (sprite) scratch.addChild(sprite);
        }
      }
    }

    /*
     * Elementos de prioridade 0 vão para a mesma textura dos tiles.
     *
     * São o chão que alguém desenhou por cima do chão: tapete, rachadura,
     * mancha, marca de passagem. Não mudam, ninguém passa por trás deles, e
     * cozê-los aqui poupa centenas de sprites vivos por mapa.
     */
    for (const event of map.events ?? []) {
      if (event.priority !== 0 || event.animated) continue;
      const sprite = this.#eventChild(event, 0);
      if (sprite) scratch.addChild(sprite);
    }

    const target = RenderTexture.create({
      width: map.width * TILE_SIZE,
      height: map.height * TILE_SIZE,
      // Arte de pixel ampliada precisa de vizinho mais próximo, ou vira borrão.
      scaleMode: 'nearest',
    });
    renderer.render({ container: scratch, target, clear: true });
    scratch.destroy({ children: true });

    // Recarregar o mesmo view (o editor faz isso a cada traço) trocava o sprite
    // e abandonava a textura anterior na GPU. Num mapa grande, pintar por alguns
    // minutos vazava centenas de megabytes.
    this.#baked?.texture.destroy(true);
    this.#baked?.destroy();

    this.#baked = new Sprite(target);
    this.container.removeChildren();

    // A paisagem por trás do mapa entra primeiro: é o que se vê onde não há
    // tile nenhum — o céu de um templo que flutua, em vez de um buraco preto.
    this.#parallax?.destroy();
    this.#parallax = this.#buildParallax(manifest, map);
    if (this.#parallax) this.container.addChild(this.#parallax);

    this.container.addChild(this.#baked, this.#animatedLayer, this.#eventLayer);

    this.#buildWater();
    this.#buildEvents();
  }

  /**
   * As folhas de `characters` que os elementos deste mapa usam.
   *
   * Carregadas de uma vez, e só as necessárias: um mapa usa meia dúzia das 51
   * folhas do acervo, e esperar por todas atrasaria a abertura sem motivo.
   */
  async #loadEventSheets(manifest: Manifest, map: MapData): Promise<void> {
    const wanted = new Set<string>();
    for (const event of map.events ?? []) {
      if (event.sheet) wanted.add(event.sheet);
    }

    await Promise.all(
      [...wanted].map(async (sheet) => {
        const entry = manifest.images['characters']?.[sheet];
        if (!entry) return;
        try {
          await Assets.load<Texture>(`${ASSETS_BASE}/img/characters/${entry.file}`);
        } catch {
          // Uma folha que falta não pode derrubar o mapa inteiro: o elemento
          // simplesmente não aparece, como acontecia com todos até agora.
        }
      }),
    );
  }

  /** A paisagem de fundo, na GPU antes de alguém pedir por ela. */
  async #loadParallax(manifest: Manifest, map: MapData): Promise<void> {
    if (!map.parallax) return;
    const entry = manifest.images['parallaxes']?.[map.parallax];
    if (!entry) return;
    try {
      await Assets.load<Texture>(`${ASSETS_BASE}/img/parallaxes/${entry.file}`);
    } catch {
      // Sem paisagem o mapa abre como sempre abriu: com o fundo vazio.
    }
  }

  /** A paisagem de fundo do mapa, repetida para cobrir a área toda. */
  #buildParallax(manifest: Manifest, map: MapData): TilingSprite | null {
    if (!map.parallax) return null;
    const entry = manifest.images['parallaxes']?.[map.parallax];
    if (!entry) return null;

    const texture = Assets.get<Texture>(`${ASSETS_BASE}/img/parallaxes/${entry.file}`);
    if (!texture) return null;

    return new TilingSprite({
      texture,
      width: map.width * TILE_SIZE,
      height: map.height * TILE_SIZE,
    });
  }

  /** O sprite de um elemento — folha de personagem ou tile do próprio tileset. */
  #eventChild(event: MapEventData, frame: number): Sprite | null {
    if (event.tileId) {
      const pieces = tilePieces(
        event.tileId,
        event.x * TILE_SIZE,
        event.y * TILE_SIZE,
        frame,
      );
      const piece = pieces[0];
      if (!piece || pieces.length !== 1) {
        // Autotile como gráfico de evento existe, mas rende vários quadrantes:
        // sai num container próprio para não perder pedaço.
        const group = new Container();
        for (const part of pieces) {
          const sprite = pieceSprite(this.#sheets, part);
          if (sprite) group.addChild(sprite);
        }
        if (group.children.length === 0) {
          group.destroy();
          return null;
        }
        // O chamador só sabe empilhar Sprite; um container vazio por fora
        // resolve sem espalhar o caso especial pelo resto do desenho.
        const holder = new Sprite();
        holder.addChild(group);
        return holder;
      }
      return pieceSprite(this.#sheets, piece);
    }

    return this.#manifest ? eventSprite(this.#manifest, event, frame) : null;
  }

  /**
   * Monta os elementos que não estão cozidos na textura, uma vez por mapa.
   *
   * São os que ficam na altura das unidades ou acima delas, mais os que animam
   * parados — a tocha que pisca. Os de chão já foram assados junto com os tiles.
   * Os parados nascem aqui e ficam; os que animam ganham uma vaga própria, que
   * `#paintEvents` reocupa a cada quadro sem tirá-los do lugar na pilha.
   */
  #buildEvents(): void {
    this.#eventLayer.removeChildren().forEach((child) => child.destroy({ children: true }));
    this.overlay.removeChildren().forEach((child) => child.destroy({ children: true }));
    this.#animatedEvents = [];

    const map = this.#map;
    if (!map) return;

    for (const event of map.events ?? []) {
      const baked = event.priority === 0 && !event.animated;
      if (baked) continue;

      // Prioridade 2 é "acima do personagem": copa de árvore, arco de portal,
      // telhado. Vai para a camada que a cena pendura em cima das unidades.
      const layer = event.priority >= 2 ? this.overlay : this.#eventLayer;

      if (animates(event)) {
        const slot = new Container();
        layer.addChild(slot);
        this.#animatedEvents.push({ event, slot });
        continue;
      }

      const sprite = this.#eventChild(event, this.#frame);
      if (sprite) layer.addChild(sprite);
    }

    this.#paintEvents();
  }

  /** Redesenha só os elementos que animam, com recortes do cache. */
  #paintEvents(): void {
    for (const { event, slot } of this.#animatedEvents) {
      slot.removeChildren().forEach((child) => child.destroy({ children: true }));
      const sprite = this.#eventChild(event, this.#frame);
      if (sprite) slot.addChild(sprite);
    }
  }

  /**
   * Monta a água uma vez por mapa. Os demais tiles já estão cozidos.
   *
   * Antes ela era desmontada e remontada inteira a cada virada da maré — num
   * mapa de 100×100 com lago, 31 mil sprites e texturas novos a cada 400ms.
   */
  #buildWater(): void {
    this.#animatedLayer.removeChildren().forEach((child) => child.destroy());
    this.#water = [];

    const map = this.#map;
    if (!map) return;

    for (let layer = 0; layer < map.layers.length; layer++) {
      for (let index = 0; index < map.width * map.height; index++) {
        const tileId = map.layers[layer]?.[index] ?? 0;
        if (tileId === 0 || !isAnimated(tileId)) continue;

        const dx = (index % map.width) * TILE_SIZE;
        const dy = Math.floor(index / map.width) * TILE_SIZE;
        const frames = Array.from({ length: WATER_FRAMES }, (_, frame) =>
          tilePieces(tileId, dx, dy, frame).map((piece) => pieceTexture(this.#sheets, piece)),
        );
        // O quadro não mexe no número nem na posição dos quadrantes, só em
        // qual pedaço da folha cada um mostra.
        const sprites = tilePieces(tileId, dx, dy).map((piece) => {
          const sprite = new Sprite();
          sprite.position.set(piece.dx, piece.dy);
          this.#animatedLayer.addChild(sprite);
          return sprite;
        });
        this.#water.push({ sprites, frames });
      }
    }

    this.#paintWater();
  }

  /** Mostra o quadro atual da água. Não cria nem destrói nada. */
  #paintWater(): void {
    for (const tile of this.#water) {
      const textures = tile.frames[this.#frame] ?? [];
      tile.sprites.forEach((sprite, i) => {
        const texture = textures[i] ?? null;
        sprite.visible = texture !== null;
        if (texture) sprite.texture = texture;
      });
    }
  }

  /** Avança a animação da água. `deltaMs` é o tempo desde o último quadro. */
  update(deltaMs: number): void {
    this.#elapsed += deltaMs;
    if (this.#elapsed < WATER_FRAME_MS) return;

    this.#elapsed -= WATER_FRAME_MS;
    this.#frame = (this.#frame + 1) % WATER_FRAMES;
    this.#paintWater();
    this.#paintEvents();
  }

  get pixelWidth(): number {
    return (this.#map?.width ?? 0) * TILE_SIZE;
  }

  get pixelHeight(): number {
    return (this.#map?.height ?? 0) * TILE_SIZE;
  }

  destroy(): void {
    this.#baked?.texture.destroy(true);
    this.container.destroy({ children: true });
    this.overlay.destroy({ children: true });
  }
}
